import type { AcademicCalendar, Bell } from "../shared/weeks";
import type { Lesson } from "./types";

const format = (iso: string, options: Intl.DateTimeFormatOptions) =>
  new Date(`${iso}T12:00:00Z`).toLocaleDateString("ru-RU", {
    ...options,
    timeZone: "UTC",
  });
export const longDate = (iso: string) =>
  format(iso, { day: "numeric", month: "long" });
export const shortDate = (iso: string) =>
  format(iso, { day: "numeric", month: "short" }).replace(" ", "\u00a0");
export const dayNumber = (iso: string) => format(iso, { day: "numeric" });

/** "12–18 октября" or "28 сентября – 4 октября". */
export function weekSpan(start: string, end: string) {
  const sameMonth = start.slice(0, 7) === end.slice(0, 7);
  return sameMonth
    ? `${dayNumber(start)}–${longDate(end)}`
    : `${longDate(start)} – ${longDate(end)}`;
}

export const plural = (n: number, forms: [string, string, string]) => {
  const tail = n % 100;
  return tail >= 11 && tail <= 14
    ? forms[2]
    : n % 10 === 1
      ? forms[0]
      : n % 10 >= 2 && n % 10 <= 4
        ? forms[1]
        : forms[2];
};

export const minutes = (time: string) => {
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
};

export function bellFor(calendar: AcademicCalendar | null, start: string) {
  return calendar?.bells.find((b) => b.start === start) || null;
}

export function pairLabel(bell: Bell | null, lesson: Lesson) {
  if (bell) return `${bell.pair} пара`;
  const session = String(lesson.pair || "").match(/\d/);
  return session ? `${session[0]} пара` : "";
}

/** Whether the lesson needs a dated status at all: plain weekly lessons don't. */
export const hasWeekLimit = (lesson: Lesson) =>
  Boolean(lesson.condition || lesson.date || lesson.dates?.length);

/** Human status from concrete dates, relative to today. */
export function lessonStatus(dates: string[], today: string) {
  if (!dates.length) return "Нет занятий в этом семестре";
  const upcoming = dates.filter((d) => d >= today);
  if (dates.length === 1)
    return dates[0] < today
      ? `Прошло ${shortDate(dates[0])}`
      : dates[0] === today
        ? "Сегодня"
        : longDate(dates[0]);
  if (!upcoming.length)
    return `Занятия закончились · последнее ${shortDate(dates.at(-1)!)}`;
  if (dates[0] > today)
    return `Начнётся ${shortDate(dates[0])} · ${dates.length} ${plural(dates.length, ["занятие", "занятия", "занятий"])}`;
  if (upcoming.length === 1)
    return `Последнее занятие — ${shortDate(upcoming[0])}`;
  return `Ещё ${upcoming.length} ${plural(upcoming.length, ["занятие", "занятия", "занятий"])} · до ${shortDate(upcoming.at(-1)!)}`;
}

/** Minutes since midnight in Minsk, whatever the device time zone. */
export const clockMinutes = (now: number) => {
  const [h, m] = new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    timeZone: "Europe/Minsk",
  })
    .format(now)
    .split(":")
    .map(Number);
  return h * 60 + m;
};

/** Whole days from today to a date (both YYYY-MM-DD). */
export const daysUntil = (today: string, iso: string) =>
  Math.round(
    (Date.parse(`${iso}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86400000,
  );
export const countdown = (days: number) =>
  days === 0
    ? "сегодня"
    : days === 1
      ? "завтра"
      : days < 0
        ? "прошло"
        : `через ${days} ${plural(days, ["день", "дня", "дней"])}`;
